/**
 * Per-report-type preference validators — the pure half of the
 * `components/siterag/preferences/*Preferences.tsx` forms.
 *
 * Moved verbatim out of the form components so server code (report setup
 * coverage, the Cloud Run worker bundles) can validate saved preferences
 * without importing React UI. The form modules re-export their validator and
 * `preferences/index.tsx` re-exports `validatePreferencesFor`, so UI imports
 * are unchanged.
 */
import type { ReportTypeId } from '../../config/reportTypes';
import type {
  CustomBriefPreferences,
  EnvironmentalPreferences,
  ExecutiveSummaryPreferences,
  FeasibilityPreferences,
  FinancialPreferences,
  InterconnectionPreferences,
  PowerAvailabilityPreferences,
  TestFitPreferences,
  TypedPreferencesByType,
  WaterResourcePreferences,
} from '../../types/siteRag';

export interface ValidationResult {
  valid: boolean;
  /** Human-readable labels for the footer-banner display. */
  missingRequired: string[];
  /**
   * Machine-readable field keys for inline highlighting (CU2). When
   * present, `FieldRow` reads this via PrefsValidationContext to add a
   * red border + Required badge + aria-invalid on the offending field.
   * Legacy validators may omit this — the field-row falls back to its
   * static `required` prop.
   */
  missingRequiredKeys?: string[];
}

export function validateFeasibilityPreferences(_value: FeasibilityPreferences | undefined): ValidationResult {
  // SSOT — the previously-required `projectSizeMW` lives on
  // SiteEntity.projectIntent.powerRequirementMW. The wizard's
  // missing-intent banner (SSOT-9) handles the "site lacks MW" case.
  // FeasibilityPreferences has no required fields after SSOT.
  return { valid: true, missingRequired: [], missingRequiredKeys: [] };
}

export function validateEnvironmentalPreferences(value: EnvironmentalPreferences | undefined): ValidationResult {
  // PB2: defensive — accept undefined.
  const v = (value ?? {}) as EnvironmentalPreferences;
  const missingRequired: string[] = [];
  const missingRequiredKeys: string[] = [];
  if (!v.expectedNepaPathway) {
    missingRequired.push('Expected NEPA pathway');
    missingRequiredKeys.push('expectedNepaPathway');
  }
  return { valid: missingRequired.length === 0, missingRequired, missingRequiredKeys };
}

export function validateTestFitPreferences(value: TestFitPreferences | undefined): ValidationResult {
  // PB2: defensive — accept undefined.
  const v = (value ?? {}) as TestFitPreferences;
  const missingRequired: string[] = [];
  const missingRequiredKeys: string[] = [];
  if (v.targetFootprintAcres === undefined || !Number.isFinite(v.targetFootprintAcres) || v.targetFootprintAcres <= 0) {
    missingRequired.push('Target footprint (acres)');
    missingRequiredKeys.push('targetFootprintAcres');
  }
  if (v.phasing === 'phased' && (v.phaseCount === undefined || v.phaseCount < 2)) {
    missingRequired.push('Phase count (2 or more for phased buildout)');
    missingRequiredKeys.push('phaseCount');
  }
  return { valid: missingRequired.length === 0, missingRequired, missingRequiredKeys };
}

export function validatePowerAvailabilityPreferences(value: PowerAvailabilityPreferences | undefined): ValidationResult {
  // SSOT — `powerRequirementMW` lives on projectIntent now; the wizard's
  // missing-intent banner (SSOT-9) handles "site lacks MW".
  // btmStrategy remains required — it's a report-type-only branching
  // signal that doesn't have an intent equivalent.
  const v = (value ?? {}) as PowerAvailabilityPreferences;
  const missingRequired: string[] = [];
  const missingRequiredKeys: string[] = [];
  if (!v.btmStrategy) {
    missingRequired.push('BTM strategy');
    missingRequiredKeys.push('btmStrategy');
  }
  return { valid: missingRequired.length === 0, missingRequired, missingRequiredKeys };
}

export function validateWaterResourcePreferences(_value: WaterResourcePreferences | undefined): ValidationResult {
  // SSOT — coolingType lives on projectIntent now; no required fields
  // remain on WaterResourcePreferences. Wizard's missing-intent banner
  // (SSOT-9) handles the "site lacks coolingStrategy" case.
  return { valid: true, missingRequired: [], missingRequiredKeys: [] };
}

export function validateInterconnectionPreferences(value: InterconnectionPreferences | undefined): ValidationResult {
  // PB2: defensive — accept undefined.
  const v = (value ?? {}) as InterconnectionPreferences;
  const missingRequired: string[] = [];
  const missingRequiredKeys: string[] = [];
  if (v.bandwidthGbps === undefined || !Number.isFinite(v.bandwidthGbps) || v.bandwidthGbps <= 0) {
    missingRequired.push('Bandwidth (Gbps)');
    missingRequiredKeys.push('bandwidthGbps');
  }
  return { valid: missingRequired.length === 0, missingRequired, missingRequiredKeys };
}

export function validateCustomBriefPreferences(value: CustomBriefPreferences | undefined): ValidationResult {
  const missingRequired: string[] = [];
  if (!value?.briefName?.trim()) missingRequired.push('briefName');
  const questions = (value?.questions ?? []).filter(q => q.text.trim().length > 0);
  if (questions.length === 0) missingRequired.push('questions');
  return { valid: missingRequired.length === 0, missingRequired };
}

export function validateExecutiveSummaryPreferences(
  value: ExecutiveSummaryPreferences | undefined,
): ValidationResult {
  const ids = (value?.sourceReportIds ?? []).filter(Boolean);
  if (ids.length === 0) {
    return { valid: false, missingRequired: ['sourceReportIds'] };
  }
  return { valid: true, missingRequired: [] };
}

export function validateFinancialPreferences(value: FinancialPreferences | undefined): ValidationResult {
  // Every field is optional — the engine falls back to the resolved WACC as the
  // hurdle, and the report body is computed from the SAVED financial model.
  void value;
  return { valid: true, missingRequired: [], missingRequiredKeys: [] };
}

/** Type-keyed validator dispatch. Returns `{ valid, missingRequired }`. */
export function validatePreferencesFor(
  reportType: ReportTypeId,
  value: TypedPreferencesByType[ReportTypeId] | undefined,
): ValidationResult {
  const v = (value ?? {}) as any;
  switch (reportType) {
    case 'feasibility':         return validateFeasibilityPreferences(v);
    case 'supply-chain-risk':   return validateFeasibilityPreferences(v);
    case 'environmental':       return validateEnvironmentalPreferences(v);
    case 'test-fit':            return validateTestFitPreferences(v);
    case 'power-availability':  return validatePowerAvailabilityPreferences(v);
    case 'water-resource':      return validateWaterResourcePreferences(v);
    case 'interconnection':     return validateInterconnectionPreferences(v);
    case 'custom-brief':        return validateCustomBriefPreferences(v);
    case 'executive-summary':   return validateExecutiveSummaryPreferences(v);
    case 'financial':           return validateFinancialPreferences(v);
    default:                    return { valid: true, missingRequired: [] };
  }
}
